/**
 * Custom File Uploader
 * Drag-and-drop file upload with shape-aware preview masking
 */

(() => {
  'use strict';

  const API_ENDPOINT = window.POPSIGNS_API_ENDPOINT || 'https://fz8y24ewil.execute-api.us-east-1.amazonaws.com/staging';
  const DEBUG = window.PITCHPRINT_DEBUG || false;
  const log = (...args) => DEBUG && console.log('[FileUploader]', ...args);

  class CustomFileUploader {
    constructor(container) {
      this.container = container;
      this.previewRenderer = new window.FileUploadPreviewRenderer();
      this.stateManager = window.PitchPrintStateManager;

      // Detect custom config first (before shape detection which depends on it)
      this._customConfig = this.detectCustomConfig();

      // Cache S3 URLs per size key to avoid re-processing when switching back
      this._sizeUrlCache = {};
      this._lastSizeKey = this.getSizeCacheKey();

      this.state = {
        mode: 'upload_now', // 'upload_now' or 'send_later'
        sides: 'single', // 'single' or 'double'
        shape: this.detectShape(),
        customConfig: this._customConfig, // Per-variant aspect ratio & corner config
        proofRequested: false, // For unconfigured shapes fallback
        activeTab: 'front', // Track which side is shown in preview
        files: {
          front: null,
          back: null
        },
        uploads: {
          front: null,
          back: null
        },
        uploadProgress: {
          front: 0,
          back: 0
        }
      };

      this.init();
    }

    init() {
      log('Initializing file uploader');
      this.state.sides = this.detectSides(); // Auto-detect on init
      this.render();
      this.attachEventListeners();
      this.watchVariantChanges(); // Watch for variant changes
      this.watchDesignMethodChanges(); // Watch for design method toggle
    }

    /**
     * Detect custom preview config from product metafield
     * Matches current variant size against upload_preview_config.sizes keys
     * @returns {object|null} Matched config { width, height, corners } or null
     */
    detectCustomConfig() {
      let config = window.UPLOAD_PREVIEW_CONFIG;
      if (!config) {
        log('No UPLOAD_PREVIEW_CONFIG found on window');
        return null;
      }
      log('UPLOAD_PREVIEW_CONFIG found:', typeof config, config);

      // Handle string values (in case metafield type is text instead of json)
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config);
          window.UPLOAD_PREVIEW_CONFIG = config;
        } catch (e) {
          log('Failed to parse UPLOAD_PREVIEW_CONFIG string:', e);
          return null;
        }
      }

      if (!config.sizes) return null;

      // Get current size from variant options
      const sizeValue = this.getCurrentSizeValue();
      if (!sizeValue) {
        log('No size value found for custom config matching');
        return null;
      }

      // Normalize size for matching: "12\" x 18\"" → "12x18", "12 x 18" → "12x18"
      const normalized = sizeValue.replace(/["\s]+/g, '').replace(/x/gi, 'x').toLowerCase();
      log('Normalized size for config matching:', normalized);

      // Try exact match first, then fuzzy match
      for (const [key, value] of Object.entries(config.sizes)) {
        const normalizedKey = key.replace(/["\s]+/g, '').replace(/x/gi, 'x').toLowerCase();
        if (normalizedKey === normalized) {
          log('Custom config matched:', key, value);
          return value;
        }
      }

      // Try matching just the numbers (e.g., "12x18" matches key containing both "12" and "18")
      const numbers = normalized.match(/\d+/g);
      if (numbers && numbers.length >= 2) {
        for (const [key, value] of Object.entries(config.sizes)) {
          const keyNumbers = key.match(/\d+/g);
          if (keyNumbers && keyNumbers.length >= 2 &&
              keyNumbers[0] === numbers[0] && keyNumbers[1] === numbers[1]) {
            log('Custom config fuzzy matched:', key, value);
            return value;
          }
        }
      }

      log('No custom config match found for size:', sizeValue);
      return null;
    }

    /**
     * Get the current size option value from variant selectors
     */
    getCurrentSizeValue() {
      const sizeOptionNames = ['Choose Your Size', 'Size', 'Dimensions'];

      // Try card-style variant selectors (active cards with data-value)
      for (const name of sizeOptionNames) {
        const group = document.querySelector(`[data-option-name="${name}"]`);
        if (group) {
          const activeCard = group.querySelector('.card-p-style.active');
          if (activeCard && activeCard.dataset.value) {
            log('Size from active card:', activeCard.dataset.value);
            return activeCard.dataset.value;
          }
        }
      }

      // Try select dropdowns
      for (const name of sizeOptionNames) {
        const select = document.querySelector(`select[name="options[${name}]"]`);
        if (select && select.value) {
          return select.value;
        }
      }

      // Try radio buttons
      for (const name of sizeOptionNames) {
        const radio = document.querySelector(`input[name="options[${name}]"]:checked`);
        if (radio && radio.value) {
          return radio.value;
        }
      }

      // Fallback: extract size from variant title
      const variantSelect = document.querySelector('select[name="id"]');
      if (variantSelect) {
        const title = variantSelect.options[variantSelect.selectedIndex]?.text || '';
        const match = title.match(/(\d+)\s*[x×]\s*(\d+)/i);
        if (match) {
          return `${match[1]}x${match[2]}`;
        }
      }

      return null;
    }

    detectShape() {
      // Check for custom config first (from upload_preview_config metafield)
      if (this._customConfig) {
        if (this._customConfig.noMask) {
          log('Custom config has noMask — shape: Unconfigured (raw artwork preview)');
          return 'Unconfigured';
        }
        log('Shape detected from custom config: Custom');
        return 'Custom';
      }

      // If config exists but no variant match, show unconfigured fallback
      // (uncropped preview + proof request checkbox)
      if (window.UPLOAD_PREVIEW_CONFIG && window.UPLOAD_PREVIEW_CONFIG.sizes) {
        log('Config exists but no size match — shape: Unconfigured');
        return 'Unconfigured';
      }

      // Standard shape detection (products without upload_preview_config)
      // First check if this is a longboard variant that needs size-based detection
      const variantSelect = document.querySelector('select[name="id"]');
      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        const variantTitle = selectedOption.text.toLowerCase();

        // For longboard variants, ALWAYS check size first before shape dropdown
        if (variantTitle.includes('longboard')) {
          // Try multiple possible size option names
          const sizeSelect = document.querySelector('select[name="options[Choose Your Size]"]') ||
                            document.querySelector('select[name="options[Size]"]');

          if (sizeSelect && sizeSelect.value) {
            const sizeValue = sizeSelect.value.toLowerCase();
            log('Checking size value for longboard:', sizeValue);

            if (sizeValue.includes('36') || sizeValue.includes('36x10')) {
              log('Shape detected from size: Longboard Shape (36x10)');
              return 'Longboard Shape';
            }
            if (sizeValue.includes('20') || sizeValue.includes('20x8')) {
              log('Shape detected from size: Longboard Mini (20x8)');
              return 'Longboard Mini';
            }
          }
          // Default to Longboard Shape if size not specified
          log('Shape detected from variant title: Longboard Shape (default, no size found)');
          return 'Longboard Shape';
        }
      }

      // For non-longboard variants, check "Choose Your Shape" dropdown
      const shapeSelect = document.querySelector('select[name="options[Choose Your Shape]"]');
      if (shapeSelect && shapeSelect.value) {
        // If the dropdown says "Longboard Shape", we need to check size for Mini vs Regular
        if (shapeSelect.value === 'Longboard Shape') {
          const sizeSelect = document.querySelector('select[name="options[Choose Your Size]"]') ||
                            document.querySelector('select[name="options[Size]"]');

          if (sizeSelect && sizeSelect.value) {
            const sizeValue = sizeSelect.value.toLowerCase();
            log('Checking size value for longboard shape:', sizeValue);

            if (sizeValue.includes('20') || sizeValue.includes('20x8')) {
              log('Shape detected from size: Longboard Mini (20x8)');
              return 'Longboard Mini';
            }
            // 36x10 or other sizes default to Longboard Shape
            log('Shape detected from size: Longboard Shape (36x10 or default)');
            return 'Longboard Shape';
          }
        }

        log('Shape detected from dropdown:', shapeSelect.value);
        return shapeSelect.value;
      }

      // Try to detect from variant title for other shapes
      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        const variantTitle = selectedOption.text.toLowerCase();

        if (variantTitle.includes('circle')) {
          log('Shape detected from variant title: Circle Shape');
          return 'Circle Shape';
        }
        if (variantTitle.includes('classic')) {
          log('Shape detected from variant title: Classic Shape');
          return 'Classic Shape';
        }
      }

      // Check all option selects for shape keywords
      const options = document.querySelectorAll('[name^="options["]');
      for (const option of options) {
        const value = option.value ? option.value.toLowerCase() : '';
        if (value.includes('circle')) {
          log('Shape detected from options: Circle Shape');
          return 'Circle Shape';
        }
        if (value.includes('classic')) {
          log('Shape detected from options: Classic Shape');
          return 'Classic Shape';
        }
      }

      // Default to Classic Shape
      log('Shape defaulting to: Classic Shape');
      return 'Classic Shape';
    }

    /**
     * Compare two customConfig objects for equality
     * Used to detect when switching between sizes with different aspect ratios
     */
    customConfigsEqual(a, b) {
      if (a === b) return true;
      if (!a || !b) return false;
      if (a.width !== b.width || a.height !== b.height) return false;
      if (a.noMask !== b.noMask) return false;
      const ca = a.corners || {}, cb = b.corners || {};
      return (ca.tl || 0) === (cb.tl || 0) && (ca.tr || 0) === (cb.tr || 0) &&
             (ca.bl || 0) === (cb.bl || 0) && (ca.br || 0) === (cb.br || 0);
    }

    /**
     * Get a normalized size key for caching S3 URLs per size
     */
    getSizeCacheKey() {
      const val = this.getCurrentSizeValue();
      return val ? val.replace(/["\s']+/g, '').toLowerCase() : null;
    }

    detectSides() {
      // Check if variant has "Double Sided" in the title or options
      const variantSelect = document.querySelector('select[name="id"]');
      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        const variantTitle = selectedOption.text.toLowerCase();

        // Check for double-sided indicators
        if (variantTitle.includes('double') ||
            variantTitle.includes('2-sided') ||
            variantTitle.includes('two sided') ||
            variantTitle.includes('2 sided')) {
          log('Detected double-sided from variant title');
          return 'double';
        }
      }

      // Check variant options
      const options = document.querySelectorAll('[name^="options["]');
      for (const option of options) {
        const value = option.value ? option.value.toLowerCase() : '';
        if (value.includes('double') ||
            value.includes('2-sided') ||
            value.includes('two sided') ||
            value.includes('2 sided')) {
          log('Detected double-sided from variant option');
          return 'double';
        }
      }

      // Default to single
      log('Detected single-sided (default)');
      return 'single';
    }

    watchVariantChanges() {
      // Watch for variant selector changes
      const variantSelectors = document.querySelectorAll('select[name="id"], [name^="options["]');
      variantSelectors.forEach(select => {
        select.addEventListener('change', () => {
          // Use setTimeout to ensure card-variants-optimized.js updates complete first
          setTimeout(async () => {
            let needsRerender = false;

            // Check for sides changes
            const newSides = this.detectSides();
            if (newSides !== this.state.sides) {
              const oldSides = this.state.sides;
              this.state.sides = newSides;
              log(`Sides updated from ${oldSides} to: ${newSides}`);

              // If switching from double to single, clear back file and switch to front
              if (oldSides === 'double' && newSides === 'single') {
                this.state.files.back = null;
                this.state.uploads.back = null;
                this.state.uploadProgress.back = 0;
                this.state.activeTab = 'front';
                // Clear back-side server URLs
                if (this._croppedUrls) delete this._croppedUrls.back;
                if (this._previewUrls) delete this._previewUrls.back;
                log('Cleared back side files, URLs, and switched to front tab');
              }

              needsRerender = true;
            }

            // Save old config reference before re-detection
            const oldCustomConfig = this._customConfig;

            // Re-detect custom config for the new variant
            this._customConfig = this.detectCustomConfig();
            this.state.customConfig = this._customConfig;

            // Check for shape changes OR custom config changes
            // (detectShape() returns "Custom" for ALL custom config sizes,
            //  so we must also compare the actual config objects)
            const newShape = this.detectShape();
            const shapeStringChanged = newShape !== this.state.shape;
            const customConfigChanged = newShape === 'Custom' &&
              !this.customConfigsEqual(oldCustomConfig, this._customConfig);

            if (shapeStringChanged || customConfigChanged) {
              // Cache current S3 URLs under the PREVIOUS size key before switching
              if (customConfigChanged) {
                this.cacheSizeUrls();
              }

              const oldShape = this.state.shape;
              this.state.shape = newShape;
              log(`Shape/config updated: ${oldShape} → ${newShape}${customConfigChanged ? ' (config changed)' : ''}`);

              // When the shape string changes (e.g. Circle → Classic), the old
              // S3 cropped/preview files are for the wrong shape. Clear them so
              // stale URLs don't flow into cart properties, Klaviyo emails, or
              // My Account. Also invalidate the size cache (all cached URLs are
              // for the old shape).
              if (shapeStringChanged) {
                this._croppedUrls = {};
                this._previewUrls = {};
                this._sizeUrlCache = {};
                // Also clear stale Shopify CDN preview URL (Cart Transform image override)
                const shopifyCdnInput = document.querySelector('input[name="properties[_shopify_preview_url]"]');
                if (shopifyCdnInput) shopifyCdnInput.remove();
                log('Cleared S3 URLs, size cache, and Shopify CDN URL for shape change');
              }

              // Regenerate client-side previews with new mask (async)
              await this.regeneratePreviewsForShapeChange();

              // Re-process on server: shape change needs new crop, custom config
              // change needs new dimensions/corners. Both go through the same path.
              if (shapeStringChanged || customConfigChanged) {
                await this.reprocessForConfigChange();
              }

              needsRerender = true;
            }

            // Re-render if anything changed
            if (needsRerender) {
              this.render();
              this.attachEventListeners();
              this.emitStateChange();
            }
          }, 50); // Small delay to let card-variants-optimized.js update first
        });
      });
    }

    watchDesignMethodChanges() {
      // Listen for design method toggle changes via StateManager
      if (!this.stateManager) {
        log('⚠️ StateManager not available, cannot watch design method changes');
        return;
      }

      this.stateManager.on('design:method:changed', (event) => {
        const newMethod = event.detail;
        log(`📡 Design method changed to: ${newMethod}`);

        if (newMethod === 'pitchprint') {
          // Switched to PitchPrint - hide file uploader
          log('🙈 Hiding file uploader (PitchPrint selected)');
          if (this.container) {
            this.container.style.display = 'none';
          }

          // Clear uploaded files when switching away
          this.reset();
        } else if (newMethod === 'file_upload') {
          // Switched to file upload - show file uploader
          log('✅ Showing file uploader (File upload selected)');
          if (this.container) {
            this.container.style.display = 'block';
          }
        }
      });

      log('✅ Design method change watcher initialized');
    }

    renderLargePreview() {
      const activeSide = this.state.activeTab;
      const hasFileOnActiveSide = this.state.uploads[activeSide] !== null;
      const previewSrc = hasFileOnActiveSide ? this.state.uploads[activeSide].previewUrl : '';

      // Check if ANY side has an uploaded file (for showing/hiding the preview area)
      const hasAnyFile = this.state.uploads.front !== null || this.state.uploads.back !== null;

      // Hide the entire preview area if no files are uploaded
      if (!hasAnyFile) {
        return '';
      }

      return `
        <div class="large-preview-area">
          <div class="preview-header">
            <h3 class="preview-title">Preview</h3>
          </div>

          <div class="large-preview-container">
            ${hasFileOnActiveSide ? `
              <img class="large-preview-image static-preview" src="${previewSrc}" alt="Preview">
            ` : `
              <div class="large-preview-skeleton">
                <div class="skeleton-instructions">
                  <h4 class="skeleton-title">No File Uploaded</h4>
                  <p class="skeleton-description">Upload a file for this side to see the preview</p>
                </div>
              </div>
            `}
          </div>

          ${this.state.sides === 'double' ? `
            <div class="preview-footer">
              <div class="preview-tabs">
                <button class="preview-tab ${this.state.activeTab === 'front' ? 'active' : ''}" data-side="front">Side 1</button>
                <button class="preview-tab ${this.state.activeTab === 'back' ? 'active' : ''}" data-side="back">Side 2</button>
              </div>
            </div>
          ` : ''}

          ${this.state.shape === 'Unconfigured' ? `
            <div class="proof-request-row">
              <label class="proof-request-label">
                <input type="checkbox" class="proof-request-checkbox" ${this.state.proofRequested ? 'checked' : ''}>
                <span>Request a design proof before production</span>
              </label>
            </div>
          ` : ''}
        </div>
      `;
    }

    renderUploadRows() {
      const renderRow = (side, label) => {
        const hasFile = this.state.files[side] !== null;
        const upload = this.state.uploads[side];
        const progress = this.state.uploadProgress[side];
        const isUploading = hasFile && progress < 100;
        const isUploaded = hasFile && upload !== null;

        const rowClass = hasFile ? (isUploaded ? 'uploaded-file-item success' : 'uploaded-file-item uploading') : 'uploaded-file-item empty';

        return `
          <div class="upload-row-wrapper">
            <div class="${rowClass}" data-side="${side}">
              ${!hasFile ? `
                <!-- Empty state with new layout -->
                <div class="file-empty-left">
                  <div class="file-thumbnail skeleton">
                    <svg class="skeleton-icon" width="40" height="32" viewBox="0 0 40 32" fill="currentColor">
                      <path d="M33,0H7C3.134,0,0,3.134,0,7v18c0,3.866,3.134,7,7,7h26c3.866,0,7-3.134,7-7V7c0-3.866-3.134-7-7-7ZM2,7c0-2.761,2.239-5,5-5h26c2.761,0,5,2.239,5,5v16.61l-9.29-9.32c-.189-.187-.444-.292-.71-.29h0c-.266-.002-.521.103-.71.29l-10.29,10.3-6.29-6.3c-.389-.392-1.022-.395-1.414-.006-.002.002-.004.004-.006.006l-7.24,7.24c-.027-.176-.043-.353-.05-.53V7ZM33,30H7c-1.722-.006-3.321-.897-4.23-2.36l7.23-7.23,6.29,6.3c.389.392,1.022.395,1.414.006.002-.002.004-.004.006-.006l10.29-10.29,9.82,9.84c-.572,2.196-2.551,3.732-4.82,3.74Z"/>
                      <path d="M14,14c2.209,0,4-1.791,4-4s-1.791-4-4-4-4,1.791-4,4,1.791,4,4,4ZM14,8c1.105,0,2,.895,2,2s-.895,2-2,2-2-.895-2-2,.895-2,2-2Z"/>
                    </svg>
                  </div>
                  <div class="file-empty-info">
                    <h4 class="upload-row-label">${label}</h4>
                    <small class="file-formats-inline">PNG, JPG, PDF • Max 300MB</small>
                  </div>
                </div>
                <div class="file-empty-right">
                  <button class="file-upload-trigger" data-side="${side}">
                    <svg class="upload-icon" width="46" height="34" viewBox="0 0 92 68" fill="currentColor">
                      <path d="M46,0c-7.158,0-14.355,2.73-19.812,8.188-4.059,4.059-6.519,9.094-7.562,14.344C8.034,24.586,0,33.835,0,45c0,12.667,10.333,23,23,23h49c11.028,0,20-8.972,20-20,0-10.408-8.018-18.881-18.188-19.812.047-7.239-2.524-14.493-8.031-20C60.328,2.734,53.158,0,46,0ZM46,6c5.638,0,11.222,2.096,15.531,6.406,4.98,4.98,7.088,11.747,6.313,18.219-.207,1.644.958,3.145,2.602,3.352.122.015.244.023.367.023h1.188c7.808,0,14,6.192,14,14s-6.192,14-14,14H23c-9.424,0-17-7.576-17-17,0-8.9,6.768-16.151,15.438-16.938,1.401-.136,2.518-1.228,2.688-2.625.552-4.755,2.656-9.375,6.312-13.031,4.306-4.305,9.924-6.406,15.562-6.406ZM46,24c-1,0-1.529.322-2.031.781l-11,10c-1.27,1.179-1.248,3.092-.188,4.25,1.061,1.158,3.086,1.242,4.25.188l5.969-5.438v19.219c0,1.657,1.343,3,3,3s3-1.343,3-3v-19.219l5.969,5.438c1.164,1.054,3.128.911,4.25-.188,1.182-1.158.966-3.184-.188-4.25l-11-10c-.594-.544-1.031-.781-2.031-.781Z"/>
                    </svg>
                    Click to Upload
                  </button>
                  <small class="file-dropzone-hint">or drop files here</small>
                </div>

                <!-- Dropzone overlay for drag feedback -->
                <div class="dropzone-overlay">
                  <svg class="upload-icon" width="48" height="48" viewBox="0 0 92 68" fill="currentColor">
                    <path d="M46,0c-7.158,0-14.355,2.73-19.812,8.188-4.059,4.059-6.519,9.094-7.562,14.344C8.034,24.586,0,33.835,0,45c0,12.667,10.333,23,23,23h49c11.028,0,20-8.972,20-20,0-10.408-8.018-18.881-18.188-19.812.047-7.239-2.524-14.493-8.031-20C60.328,2.734,53.158,0,46,0ZM46,6c5.638,0,11.222,2.096,15.531,6.406,4.98,4.98,7.088,11.747,6.313,18.219-.207,1.644.958,3.145,2.602,3.352.122.015.244.023.367.023h1.188c7.808,0,14,6.192,14,14s-6.192,14-14,14H23c-9.424,0-17-7.576-17-17,0-8.9,6.768-16.151,15.438-16.938,1.401-.136,2.518-1.228,2.688-2.625.552-4.755,2.656-9.375,6.312-13.031,4.306-4.305,9.924-6.406,15.562-6.406ZM46,24c-1,0-1.529.322-2.031.781l-11,10c-1.27,1.179-1.248,3.092-.188,4.25,1.061,1.158,3.086,1.242,4.25.188l5.969-5.438v19.219c0,1.657,1.343,3,3,3s3-1.343,3-3v-19.219l5.969,5.438c1.164,1.054,3.128.911,4.25-.188,1.182-1.158.966-3.184-.188-4.25l-11-10c-.594-.544-1.031-.781-2.031-.781Z"/>
                  </svg>
                  <span>Drop file here</span>
                </div>
              ` : `
                <!-- Uploaded state -->
                <div class="file-thumbnail">
                  ${upload ? `
                    <img src="${upload.thumbnailUrl || upload.previewUrl}" alt="${label}">
                  ` : `
                    <div class="skeleton">
                      <svg class="skeleton-icon" width="40" height="32" viewBox="0 0 40 32" fill="currentColor">
                        <path d="M33,0H7C3.134,0,0,3.134,0,7v18c0,3.866,3.134,7,7,7h26c3.866,0,7-3.134,7-7V7c0-3.866-3.134-7-7-7ZM2,7c0-2.761,2.239-5,5-5h26c2.761,0,5,2.239,5,5v16.61l-9.29-9.32c-.189-.187-.444-.292-.71-.29h0c-.266-.002-.521.103-.71.29l-10.29,10.3-6.29-6.3c-.389-.392-1.022-.395-1.414-.006-.002.002-.004.004-.006.006l-7.24,7.24c-.027-.176-.043-.353-.05-.53V7ZM33,30H7c-1.722-.006-3.321-.897-4.23-2.36l7.23-7.23,6.29,6.3c.389.392,1.022.395,1.414.006.002-.002.004-.004.006-.006l10.29-10.29,9.82,9.84c-.572,2.196-2.551,3.732-4.82,3.74Z"/>
                        <path d="M14,14c2.209,0,4-1.791,4-4s-1.791-4-4-4-4,1.791-4,4,1.791,4,4,4ZM14,8c1.105,0,2,.895,2,2s-.895,2-2,2-2-.895-2-2,.895-2,2-2Z"/>
                      </svg>
                    </div>
                  `}
                </div>

                <div class="file-info">
                  <div class="file-header">
                    <svg class="file-check" width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span class="file-name">${this.state.files[side].name}</span>
                  </div>
                  <div class="file-meta">
                    <span class="file-size">Size: ${(this.state.files[side].size / 1024 / 1024).toFixed(2)}MB</span>
                    ${isUploaded ? '<span class="file-status">✓ Uploaded</span>' : ''}
                  </div>
                  ${isUploading ? `
                    <div class="file-progress-bar">
                      <span class="file-progress-fill" style="width: ${progress}%"></span>
                    </div>
                    <span class="file-progress-text">${Math.round(progress)}%</span>
                  ` : ''}
                </div>
              `}

              ${hasFile ? `
                <div class="file-actions">
                  <button class="file-replace-btn" data-side="${side}">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M1 4v6h6M23 20v-6h-6" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Replace
                  </button>
                  <button class="file-remove-btn" data-side="${side}">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="3,6 5,6 21,6" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
              ` : ''}

              <input type="file" id="file-input-${side}" accept="image/png,image/jpeg,application/pdf" class="upload-file-input" style="display: none;">
            </div>
          </div>
        `;
      };

      return `
        <div class="uploaded-files-list">
          ${renderRow('front', 'Side 1')}
          ${this.state.sides === 'double' ? renderRow('back', 'Side 2') : ''}
        </div>
      `;
    }

    renderTemplateHelp() {
      // Use templates from product metafield if available, otherwise fallback to hardcoded defaults
      let templates = [];

      if (window.PRODUCT_DESIGN_TEMPLATES && window.PRODUCT_DESIGN_TEMPLATES.length > 0) {
        // Use dynamic templates from metafield
        templates = window.PRODUCT_DESIGN_TEMPLATES.map(template => ({
          name: template.name,
          specs: template.specs,
          url: template.url
        }));
        log('Using dynamic templates from product metafield:', templates);
      } else {
        // Fallback to hardcoded templates
        templates = [
          {
            name: 'Circle Shape',
            specs: '12" diameter • 1512 x 1512 px',
            url: 'https://cdn.shopify.com/s/files/1/0044/0441/2364/files/Circle_Template.png?v=1234'
          },
          {
            name: 'Classic Shape',
            specs: '12" x 12" • 1512 x 1512 px',
            url: 'https://cdn.shopify.com/s/files/1/0044/0441/2364/files/Classic_Template.png?v=1234'
          },
          {
            name: 'Longboard Shape',
            specs: '36" x 10" • 2664 x 728 px',
            url: 'https://cdn.shopify.com/s/files/1/0044/0441/2364/files/Longboard_Template.png?v=1234'
          },
          {
            name: 'Longboard Mini',
            specs: '20" x 8" • 1512 x 594 px',
            url: 'https://cdn.shopify.com/s/files/1/0044/0441/2364/files/Longboard_Mini_Template.png?v=1234'
          }
        ];
        log('Using fallback hardcoded templates');
      }

      // If no templates available, don't render the section
      if (templates.length === 0) {
        return '';
      }

      return `
        <div class="template-help-section">
          <button class="template-help-header" type="button">
            <div class="template-help-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span>Need help? Download design templates</span>
            </div>
            <svg class="template-help-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>

          <div class="template-help-content">
            <div class="template-help-body">
              <div class="template-downloads">
                <h4>Download Templates</h4>
                <div class="template-grid">
                  ${templates.map(template => `
                    <a href="${template.url}" download="${template.name.replace(/\s+/g, '_')}_Template" class="template-download-btn">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      <div class="template-info">
                        <span class="template-name">${template.name}</span>
                        <span class="template-specs">${template.specs}</span>
                      </div>
                    </a>
                  `).join('')}
                </div>
              </div>

              <div class="template-tips">
                <h4>Design Tips</h4>
                <ul>
                  <li><strong>Resolution:</strong> Use 300 DPI for best print quality</li>
                  <li><strong>Safe Zone:</strong> Keep important elements within the inner guide area</li>
                  <li><strong>Bleed:</strong> Extend your design to the outer edges</li>
                  <li><strong>File Format:</strong> PNG or JPG recommended, PDF also accepted</li>
                  <li><strong>File Size:</strong> Maximum 300MB per file</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    render() {
      this.container.innerHTML = `
        <div class="file-upload-wrapper">
          <!-- Mode Selection -->
          <div class="upload-mode-selector">
            <div class="mode-option ${this.state.mode === 'upload_now' ? 'active' : ''}">
              <input type="radio" id="mode-upload-now" name="upload_mode" value="upload_now" ${this.state.mode === 'upload_now' ? 'checked' : ''}>
              <label for="mode-upload-now">
                <svg class="mode-icon" width="46" height="34" viewBox="0 0 92 68" fill="currentColor">
                  <path d="M46,0c-7.158,0-14.355,2.73-19.812,8.188-4.059,4.059-6.519,9.094-7.562,14.344C8.034,24.586,0,33.835,0,45c0,12.667,10.333,23,23,23h49c11.028,0,20-8.972,20-20,0-10.408-8.018-18.881-18.188-19.812.047-7.239-2.524-14.493-8.031-20C60.328,2.734,53.158,0,46,0ZM46,6c5.638,0,11.222,2.096,15.531,6.406,4.98,4.98,7.088,11.747,6.313,18.219-.207,1.644.958,3.145,2.602,3.352.122.015.244.023.367.023h1.188c7.808,0,14,6.192,14,14s-6.192,14-14,14H23c-9.424,0-17-7.576-17-17,0-8.9,6.768-16.151,15.438-16.938,1.401-.136,2.518-1.228,2.688-2.625.552-4.755,2.656-9.375,6.312-13.031,4.306-4.305,9.924-6.406,15.562-6.406ZM46,24c-1,0-1.529.322-2.031.781l-11,10c-1.27,1.179-1.248,3.092-.188,4.25,1.061,1.158,3.086,1.242,4.25.188l5.969-5.438v19.219c0,1.657,1.343,3,3,3s3-1.343,3-3v-19.219l5.969,5.438c1.164,1.054,3.128.911,4.25-.188,1.182-1.158.966-3.184-.188-4.25l-11-10c-.594-.544-1.031-.781-2.031-.781Z"/>
                </svg>
                <span>Upload Files Now</span>
              </label>
            </div>
            <div class="mode-option ${this.state.mode === 'send_later' ? 'active' : ''}">
              <input type="radio" id="mode-send-later" name="upload_mode" value="send_later" ${this.state.mode === 'send_later' ? 'checked' : ''}>
              <label for="mode-send-later">
                <svg class="mode-icon" width="46" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span>Send Files Later (via email)</span>
              </label>
            </div>
          </div>

          <!-- Upload Controls (visible only for 'upload_now') -->
          <div class="upload-controls" style="display: ${this.state.mode === 'upload_now' ? 'block' : 'none'}">
            ${this.renderUploadRows()}
            ${this.renderLargePreview()}
            ${this.renderTemplateHelp()}
          </div>

          <!-- Send Later Notice -->
          <div class="send-later-notice" style="display: ${this.state.mode === 'send_later' ? 'flex' : 'none'}">
            <p>You'll receive an email after checkout with instructions to upload your design files.</p>
          </div>

          <!-- Error Messages -->
          <div class="upload-errors" style="display: none;"></div>
        </div>
      `;
    }

    attachEventListeners() {
      // Mode selection
      this.container.querySelectorAll('input[name="upload_mode"]').forEach(radio => {
        radio.addEventListener('change', (e) => this.handleModeChange(e.target.value));
      });

      // Preview tab switching (Side 1 / Side 2)
      this.container.querySelectorAll('.preview-tab').forEach(tab => {
        tab.addEventListener('click', (e) => {
          const side = e.currentTarget.dataset.side;
          this.handleTabSwitch(side);
        });
      });

      // File inputs and upload triggers
      ['front', 'back'].forEach(side => {
        const input = this.container.querySelector(`#file-input-${side}`);
        if (input) {
          input.addEventListener('change', (e) => this.handleFileSelect(e, side));
        }

        // Upload trigger button clicks (for empty state)
        const uploadTrigger = this.container.querySelector(`.file-upload-trigger[data-side="${side}"]`);
        if (uploadTrigger) {
          uploadTrigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            input?.click();
          });
        }

        // Replace button clicks
        const replaceBtn = this.container.querySelector(`.file-replace-btn[data-side="${side}"]`);
        if (replaceBtn) {
          replaceBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            input?.click();
          });
        }

        // Remove file button
        const removeBtn = this.container.querySelector(`.file-remove-btn[data-side="${side}"]`);
        if (removeBtn) {
          removeBtn.addEventListener('click', () => this.handleRemoveFile(side));
        }

        // Drag and drop on file rows
        const fileRow = this.container.querySelector(`.uploaded-file-item[data-side="${side}"]`);
        if (fileRow) {
          fileRow.addEventListener('dragover', (e) => this.handleDragOver(e));
          fileRow.addEventListener('dragleave', (e) => this.handleDragLeave(e));
          fileRow.addEventListener('drop', (e) => this.handleDrop(e, side));
        }
      });

      // Proof request checkbox (for unconfigured shapes)
      const proofCheckbox = this.container.querySelector('.proof-request-checkbox');
      if (proofCheckbox) {
        proofCheckbox.addEventListener('change', (e) => {
          this.state.proofRequested = e.target.checked;
          log('Proof requested:', this.state.proofRequested);
          // Update hidden input
          const proofInput = document.getElementById('proof-requested-input');
          if (proofInput) proofInput.value = this.state.proofRequested ? 'true' : '';
          this.emitStateChange();
        });
      }

      // Template help toggle
      const templateHelpHeader = this.container.querySelector('.template-help-header');
      if (templateHelpHeader) {
        templateHelpHeader.addEventListener('click', () => {
          const section = this.container.querySelector('.template-help-section');
          section.classList.toggle('expanded');
        });
      }
    }

    handleTabSwitch(side) {
      this.state.activeTab = side;
      log('Tab switched to:', side);

      // Update tab active states without full re-render
      this.container.querySelectorAll('.preview-tab').forEach(tab => {
        tab.classList.toggle('active', tab.dataset.side === side);
      });

      // Swap preview image
      const previewImg = this.container.querySelector('.large-preview-image');
      const skeleton = this.container.querySelector('.large-preview-skeleton');
      const upload = this.state.uploads[side];

      if (upload) {
        if (previewImg) {
          previewImg.src = upload.previewUrl;
        } else if (skeleton) {
          // Replace skeleton with image
          skeleton.outerHTML = `<img class="large-preview-image static-preview" src="${upload.previewUrl}" alt="Preview">`;
        }
      } else {
        // No file on this side — show skeleton
        if (previewImg) {
          previewImg.outerHTML = `
            <div class="large-preview-skeleton">
              <div class="skeleton-instructions">
                <h4 class="skeleton-title">No File Uploaded</h4>
                <p class="skeleton-description">Upload a file for this side to see the preview</p>
              </div>
            </div>`;
        }
      }
    }

    handleModeChange(mode) {
      this.state.mode = mode;
      log('Mode changed to:', mode);

      // Update UI
      const uploadControls = this.container.querySelector('.upload-controls');
      const sendLaterNotice = this.container.querySelector('.send-later-notice');

      if (mode === 'upload_now') {
        uploadControls.style.display = 'block';
        sendLaterNotice.style.display = 'none';
      } else {
        uploadControls.style.display = 'none';
        sendLaterNotice.style.display = 'flex';
      }

      // Update mode options styling
      this.container.querySelectorAll('.mode-option').forEach(option => {
        option.classList.toggle('active', option.querySelector('input').value === mode);
      });

      this.emitStateChange();

      // Dispatch DOM event for analytics consumption only — separates real
      // user mode toggles from emitStateChange()'s many other call sites
      // (file processed, size changed, proof checkbox, etc.) (LEAK-B-09).
      document.dispatchEvent(new CustomEvent('upload:mode:user-changed', { detail: mode }));
    }

    handleDragOver(e) {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.add('drag-over');
    }

    handleDragLeave(e) {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.remove('drag-over');
    }

    handleDrop(e, side) {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.remove('drag-over');

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0], side);
      }
    }

    handleFileSelect(e, side) {
      const files = e.target.files;
      if (files.length > 0) {
        this.processFile(files[0], side);
      }
    }

    async processFile(file, side) {
      log('Processing file:', file.name, 'for side:', side);

      // Validate file
      const validation = this.validateFile(file);
      if (!validation.valid) {
        this.showError(validation.error);
        return;
      }

      this.state.files[side] = file;
      this.state.uploadProgress[side] = 0;

      // Clear previous upload's server-side URLs for this side.
      // Without this, replacing a file keeps the OLD preview/cropped URLs
      // until the new background process completes — but emitStateChange()
      // fires before that, so the stale URLs get passed to StateManager/cart.
      if (this._previewUrls) delete this._previewUrls[side];
      if (this._croppedUrls) delete this._croppedUrls[side];
      // Also clear the upload state so isUploadComplete() returns false
      this.state.uploads[side] = null;

      // Re-render to show uploading state
      this.render();
      this.attachEventListeners();

      // OPTIMIZATION: Start upload and preview generation in parallel for faster uploads
      // Large files (especially 300MB PDFs) can upload while preview is being generated
      try {
        this.showProgress(side, 0, 'Starting upload...');

        // Compress image before upload (PNGs at 6300px: 83MB → ~5MB JPEG). PDFs skipped.
        const uploadFile = await this.compressForUpload(file);

        // Start BOTH operations in parallel (don't wait for preview before uploading)
        // Preview uses original `file` for full quality; S3 upload uses compressed `uploadFile`
        const uploadPromise = this.uploadFile(uploadFile, null, side);

        const previewPromise = (async () => {
          // Show appropriate message based on file size
          const fileSizeMB = file.size / (1024 * 1024);
          const message = fileSizeMB > 50
            ? `Processing ${Math.round(fileSizeMB)}MB file...`
            : 'Generating preview...';

          this.showProgress(side, 5, message);

          // Generate masked preview and raw thumbnail in parallel
          // Use higher resolution for thumbnail (400px) for better quality in display
          // For "Unconfigured" shapes, skip masking — show raw artwork only
          const previewOptions = this.state.shape === 'Custom' && this.state.customConfig
            ? { customConfig: this.state.customConfig }
            : undefined;

          const [preview, rawThumbnail] = await Promise.all([
            this.state.shape === 'Unconfigured'
              ? this.previewRenderer.generateRawThumbnail(file, 1200)
                  .then(raw => ({ dataUrl: raw.dataUrl, blob: raw.blob, width: raw.width, height: raw.height, shape: 'Unconfigured', fileName: file.name }))
              : this.previewRenderer.generatePreview(file, this.state.shape, previewOptions),
            this.previewRenderer.generateRawThumbnail(file, 400)
          ]);

          log('Preview and raw thumbnail generated');

          // Store both preview (masked) and thumbnail (raw)
          this.state.uploads[side] = {
            ...(this.state.uploads[side] || {}),
            thumbnailUrl: rawThumbnail.dataUrl, // Raw artwork thumbnail
            previewUrl: preview.dataUrl         // Masked preview
          };

          // Re-render to show thumbnail while uploading continues
          this.render();
          this.attachEventListeners();

          // Smooth scroll to preview (now below upload rows)
          const previewArea = this.container.querySelector('.large-preview-area');
          if (previewArea) {
            previewArea.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }

          return { preview, rawThumbnail };
        })();

        // Wait for both upload and preview generation to complete
        const [uploadResult, previewResult] = await Promise.all([uploadPromise, previewPromise]);

        // For PDFs: upload canvas preview blob AND high-res rasterized blob to S3
        if (file.type === 'application/pdf') {
          // Upload low-res preview blob (for display)
          if (!this._previewUrls?.[side] && previewResult?.preview?.blob && uploadResult?.previewUploadUrl) {
            try {
              log('PDF detected - uploading canvas preview to S3');
              await this.uploadPreviewToS3(uploadResult.previewUploadUrl, previewResult.preview.blob);
              const previewCdnUrl = `https://img.popsigns.co/${uploadResult.previewKey}`;
              this._previewUrls = this._previewUrls || {};
              this._previewUrls[side] = previewCdnUrl;
              log('PDF preview uploaded:', previewCdnUrl);
            } catch (err) {
              console.warn('[FileUploader] PDF preview upload failed:', err);
            }
          }

          // Upload high-res rasterized PNG + trigger server-side cropping — NON-BLOCKING
          // The rasterized upload chain (upload → re-confirm → process) is for fulfillment only.
          // Canvas preview (for display) was already uploaded above. Fire-and-forget this chain.
          if (uploadResult?.rasterizedUploadUrl && this.previewRenderer._pdfRasterizedBlobPromise) {
            (async () => {
              const rasterizedBlob = await this.previewRenderer._pdfRasterizedBlobPromise;
              if (!rasterizedBlob) return;

              await this.uploadPreviewToS3(uploadResult.rasterizedUploadUrl, rasterizedBlob);
              log('PDF rasterized upload complete:', `${(rasterizedBlob.size / 1024 / 1024).toFixed(1)}MB`);

              await this.confirmUpload(uploadResult.uploadId, side, file.size, null, true);

              const processResponse = await fetch(`${API_ENDPOINT}/api/uploads/process`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Shop-Domain': window.Shopify?.shop || 'popsigns.co'
                },
                body: JSON.stringify({ uploadId: uploadResult.uploadId, side })
              });
              const processData = await processResponse.json();
              if (processData.success && processData.croppedUrl) {
                log('PDF server-side crop complete:', processData.croppedUrl);
                this._croppedUrls = this._croppedUrls || {};
                this._croppedUrls[side] = processData.croppedUrl;
                if (processData.previewUrl) {
                  this._previewUrls = this._previewUrls || {};
                  this._previewUrls[side] = processData.previewUrl;
                }
                const sizeKey = this.getSizeCacheKey();
                if (sizeKey) {
                  this._sizeUrlCache[sizeKey] = this._sizeUrlCache[sizeKey] || {};
                  this._sizeUrlCache[sizeKey][side] = {
                    croppedUrl: processData.croppedUrl,
                    previewUrl: processData.previewUrl
                  };
                }
              }
            })().catch(err => console.warn('[FileUploader] PDF background processing failed:', err));
          }
        }

        // Emit state change AFTER all processing is complete (upload + preview + PDF preview upload).
        // This ensures previewUrls are populated before upload:completed fires,
        // preventing the cart from storing raw PDF URLs as preview images.
        this.emitStateChange();

        // Switch to the uploaded side to show the preview
        this.state.activeTab = side;
        log(`Switched to ${side} side to show uploaded preview`);

        // Final re-render after both complete
        this.render();
        this.attachEventListeners();

      } catch (error) {
        console.error('[FileUploader] Error processing file:', error);
        this.showError(`Failed to process ${file.name}: ${error.message}`);
        this.state.files[side] = null;
        this.state.uploads[side] = null;
        this.render();
        this.attachEventListeners();
      }
    }

    validateFile(file) {
      const maxSize = 300 * 1024 * 1024; // 300MB
      const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf'];

      if (!allowedTypes.includes(file.type)) {
        return {
          valid: false,
          error: `Invalid file type. Please upload PNG, JPG, or PDF files.`
        };
      }

      if (file.size > maxSize) {
        return {
          valid: false,
          error: `File too large. Maximum size is 300MB.`
        };
      }

      return { valid: true };
    }

    /**
     * Compress image file before S3 upload to reduce upload time.
     * Converts large PNGs to JPEG at full resolution — preserves dimensions, dramatically reduces size.
     * PDFs are skipped (uploaded as-is). Small files (<5MB within dimension limit) are skipped.
     * @param {File} file - The original file
     * @param {number} maxDimension - Max width/height in pixels (default 6300 for Popsigns products)
     * @param {number} quality - JPEG quality 0-1 (default 0.92)
     * @returns {File} Compressed file or original if no compression needed
     */
    async compressForUpload(file, maxDimension = 6300, quality = 0.92) {
      // Skip PDFs — they get uploaded as-is (rasterized separately by PDF.js)
      if (file.type === 'application/pdf') return file;

      const img = await this.previewRenderer.loadImage(file);

      // Skip if already small enough
      if (img.width <= maxDimension && img.height <= maxDimension && file.size < 5 * 1024 * 1024) {
        log('File already small enough, skipping compression:', `${(file.size / 1024 / 1024).toFixed(1)}MB`);
        return file;
      }

      const canvas = document.createElement('canvas');
      const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const outputType = 'image/jpeg';
      const blob = await new Promise(resolve => canvas.toBlob(resolve, outputType, quality));

      const compressedName = file.name.replace(/\.\w+$/, '.jpg');
      const compressedFile = new File([blob], compressedName, { type: outputType });
      log(`Compressed for upload: ${(file.size / 1024 / 1024).toFixed(1)}MB → ${(blob.size / 1024 / 1024).toFixed(1)}MB (${canvas.width}x${canvas.height})`);
      return compressedFile;
    }

    async uploadFile(file, preview, side) {
      try {
        log('Starting upload for:', file.name);

        // Step 1: Get presigned URL
        this.showProgress(side, 10, 'Preparing upload...');

        const presignResponse = await this.getPresignedUrl(file, side);

        log('Got presigned URL:', presignResponse.uploadId);

        // Step 2: Upload original file to S3
        this.showProgress(side, 30, 'Uploading file...');

        await this.uploadToS3(presignResponse.uploadUrl, file, (progress) => {
          this.showProgress(side, 30 + (progress * 0.6), 'Uploading...');
        });

        // Step 3: Skip preview upload for now (preview is null when running in parallel)
        // Preview will be available after parallel generation completes
        this.showProgress(side, 95, 'Finalizing...');

        // Step 4: Confirm upload (preview URL will be added later if needed)
        await this.confirmUpload(presignResponse.uploadId, side, file.size, null);

        // Step 5: Request server-side shape cropping (full resolution) — NON-BLOCKING
        // Cropped URL is for fulfillment/production, not customer display (preview already visible).
        // Fire-and-forget: result stored via .then() so it's available if customer waits before ATC.
        // Guard: capture uploadId so stale crop results from a previous upload are discarded.
        const cropUploadId = presignResponse.uploadId;
        fetch(`${API_ENDPOINT}/api/uploads/process`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shop-Domain': window.Shopify?.shop || 'popsigns.co'
          },
          body: JSON.stringify({
            uploadId: presignResponse.uploadId,
            side
          })
        })
          .then(r => r.json())
          .then(processData => {
            // Discard stale result if a newer upload has replaced this side
            // or if the upload was cleared (e.g., user started a replacement).
            // uploadId is set synchronously in uploadFile() BEFORE this .then()
            // can resolve, so currentId === cropUploadId only for the current upload.
            const currentId = this.state.uploads[side]?.uploadId;
            if (currentId !== cropUploadId) {
              log('Discarding stale crop result for', cropUploadId, '(current:', currentId, ')');
              return;
            }
            if (processData.success && processData.croppedUrl) {
              log('Server-side crop complete:', processData.croppedUrl);
              this._croppedUrls = this._croppedUrls || {};
              this._croppedUrls[side] = processData.croppedUrl;
              if (processData.previewUrl) {
                this._previewUrls = this._previewUrls || {};
                this._previewUrls[side] = processData.previewUrl;
                log('Preview thumbnail:', processData.previewUrl);
              }
              const sizeKey = this.getSizeCacheKey();
              if (sizeKey) {
                this._lastSizeKey = sizeKey;
                this._sizeUrlCache[sizeKey] = this._sizeUrlCache[sizeKey] || {};
                this._sizeUrlCache[sizeKey][side] = {
                  croppedUrl: processData.croppedUrl,
                  previewUrl: processData.previewUrl
                };
              }
              // Re-emit state so StateManager gets the server-side preview URL
              this.emitStateChange();
            } else if (processData.skipped) {
              log('Cropping skipped:', processData.reason);
            }
          })
          .catch(err => console.warn('[FileUploader] Background processing failed:', err));

        this.showProgress(side, 100, 'Complete!');

        // Store upload data - preserve existing preview/thumbnail if already set
        const currentPreview = this.state.uploads[side]?.previewUrl;
        const currentThumbnail = this.state.uploads[side]?.thumbnailUrl;

        this.state.uploads[side] = {
          ...(this.state.uploads[side] || {}),
          uploadId: presignResponse.uploadId,
          originalUrl: presignResponse.originalKey,
          fileKey: presignResponse.originalKey, // S3 key for the uploaded file
          // Keep existing preview/thumbnail if already generated, otherwise set to null
          previewUrl: currentPreview || null,
          thumbnailUrl: currentThumbnail || null,
          fileName: file.name,
          fileSize: file.size
        };

        // NOTE: emitStateChange() is NOT called here — it's called from processFile()
        // AFTER all processing (including PDF preview upload) is complete.
        // This prevents a race condition where the upload:completed event fires
        // before the PDF preview URL is available, causing the cart to store
        // the raw PDF URL as the preview (which can't render as an <img>).

        log('Upload complete:', presignResponse.uploadId);
        return presignResponse; // Return for parallel processing

      } catch (error) {
        console.error('[FileUploader] Upload failed:', error);
        this.showError(`Upload failed: ${error.message}`);
        throw error;
      }
    }

    async getPresignedUrl(file, side) {
      const response = await fetch(`${API_ENDPOINT}/api/uploads/presign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shop-Domain': window.Shopify?.shop || 'popsigns.co'
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          customerId: this.getCustomerId(),
          shape: this.state.shape,
          customConfig: this.state.customConfig || undefined,
          side: side
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to get presigned URL: ${response.statusText}`);
      }

      return await response.json();
    }

    async uploadToS3(presignedUrl, file, onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = e.loaded / e.total;
            onProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
    }

    async uploadPreviewToS3(previewUploadUrl, previewBlob) {
      if (!previewUploadUrl || !previewBlob) return null;

      log('Uploading preview image to S3...');
      const xhr = new XMLHttpRequest();
      return new Promise((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            log('Preview image uploaded to S3');
            resolve(true);
          } else {
            reject(new Error(`Preview upload failed: ${xhr.status}`));
          }
        });
        xhr.addEventListener('error', () => reject(new Error('Preview upload failed')));
        xhr.open('PUT', previewUploadUrl);
        xhr.setRequestHeader('Content-Type', 'image/png');
        xhr.send(previewBlob);
      });
    }

    async confirmUpload(uploadId, side, fileSize, previewUrl, rasterizedUploaded) {
      const response = await fetch(`${API_ENDPOINT}/api/uploads/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shop-Domain': window.Shopify?.shop || 'popsigns.co'
        },
        body: JSON.stringify({
          uploadId,
          side,
          fileSize,
          previewUrl,
          rasterizedUploaded: rasterizedUploaded || undefined,
          maskApplied: this.state.shape
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to confirm upload: ${response.statusText}`);
      }

      return await response.json();
    }

    getCustomerId() {
      // Get customer ID from Shopify or generate temporary ID
      // Always return as string for DynamoDB compatibility
      const customerId = window.ShopifyAnalytics?.meta?.page?.customerId;
      return customerId ? String(customerId) : `temp-${Date.now()}`;
    }

    showProgress(side, percent, text) {
      this.state.uploadProgress[side] = percent;

      // Update progress bar if row exists
      const fileRow = this.container.querySelector(`.uploaded-file-item[data-side="${side}"]`);
      if (fileRow) {
        const progressFill = fileRow.querySelector('.file-progress-fill');
        const progressText = fileRow.querySelector('.file-progress-text');

        if (progressFill) progressFill.style.width = `${percent}%`;
        if (progressText) progressText.textContent = text || `${Math.round(percent)}%`;
      }
    }

    handleRemoveFile(side) {
      this.state.files[side] = null;
      this.state.uploads[side] = null;
      this.state.uploadProgress[side] = 0;
      this._sizeUrlCache = {}; // Clear size cache when file removed
      this.render();
      this.attachEventListeners();
      this.emitStateChange();
    }

    async regeneratePreviewsForShapeChange() {
      // Regenerate previews for all uploaded files with the new shape mask
      const sides = ['front', 'back'];

      // Show loading overlay on preview area
      this.showPreviewLoading(true, 'Regenerating previews for new shape...');

      for (const side of sides) {
        // Only regenerate if we have a file uploaded
        if (this.state.files[side]) {
          log(`Regenerating preview for ${side} with new shape: ${this.state.shape}`);

          try {
            // Update loading message with current side
            const fileSizeMB = this.state.files[side].size / (1024 * 1024);
            const message = fileSizeMB > 50
              ? `Regenerating ${side} preview (${Math.round(fileSizeMB)}MB)...`
              : `Regenerating ${side} preview...`;
            this.showPreviewLoading(true, message);

            // Generate new masked preview (thumbnail stays as raw artwork)
            const previewOptions = this.state.shape === 'Custom' && this.state.customConfig
              ? { customConfig: this.state.customConfig }
              : undefined;

            let preview;
            if (this.state.shape === 'Unconfigured') {
              const raw = await this.previewRenderer.generateRawThumbnail(this.state.files[side], 1200);
              preview = { dataUrl: raw.dataUrl, width: raw.width, height: raw.height };
            } else {
              preview = await this.previewRenderer.generatePreview(
                this.state.files[side],
                this.state.shape,
                previewOptions
              );
            }

            // Update only the preview URL (keep raw thumbnail unchanged)
            if (this.state.uploads[side]) {
              this.state.uploads[side].previewUrl = preview.dataUrl;
              // thumbnailUrl stays as raw artwork - don't update it
            }

            log(`Preview regenerated for ${side}`);
          } catch (error) {
            console.error(`[FileUploader] Failed to regenerate preview for ${side}:`, error);
          }
        }
      }

      // Hide loading overlay
      this.showPreviewLoading(false);
    }

    /**
     * Cache current S3 URLs under the previous size key before switching sizes.
     * Called BEFORE detectCustomConfig() updates _lastSizeKey.
     */
    cacheSizeUrls() {
      const sizeKey = this._lastSizeKey;
      if (!sizeKey) return;

      for (const side of ['front', 'back']) {
        if (this._croppedUrls?.[side] || this._previewUrls?.[side]) {
          this._sizeUrlCache[sizeKey] = this._sizeUrlCache[sizeKey] || {};
          this._sizeUrlCache[sizeKey][side] = {
            croppedUrl: this._croppedUrls?.[side] || null,
            previewUrl: this._previewUrls?.[side] || null
          };
        }
      }
      log(`Cached S3 URLs for size: ${sizeKey}`);
    }

    /**
     * Re-process uploaded files on the server after a shape or config change.
     * Checks the size URL cache first — only hits the server if not cached.
     */
    async reprocessForConfigChange() {
      const sizeKey = this.getSizeCacheKey();
      this._lastSizeKey = sizeKey;

      // Check cache first — restore if we already processed this size
      if (sizeKey && this._sizeUrlCache[sizeKey]) {
        const cached = this._sizeUrlCache[sizeKey];
        log(`Restoring cached S3 URLs for size: ${sizeKey}`);
        for (const side of ['front', 'back']) {
          if (cached[side]) {
            this._croppedUrls = this._croppedUrls || {};
            this._previewUrls = this._previewUrls || {};
            this._croppedUrls[side] = cached[side].croppedUrl;
            this._previewUrls[side] = cached[side].previewUrl;
          }
        }
        this.emitStateChange();
        return;
      }

      // No cache — re-process each side via server
      for (const side of ['front', 'back']) {
        const upload = this.state.uploads[side];
        if (!upload?.uploadId) continue;

        log(`Re-processing ${side} for size: ${sizeKey}`);
        try {
          const resp = await fetch(`${API_ENDPOINT}/api/uploads/process`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shop-Domain': window.Shopify?.shop || 'popsigns.co'
            },
            body: JSON.stringify({
              uploadId: upload.uploadId,
              side,
              sizeKey,
              customConfig: this.state.customConfig,
              shape: this.state.shape
            })
          });
          const data = await resp.json();
          if (data.success && data.croppedUrl) {
            this._croppedUrls = this._croppedUrls || {};
            this._croppedUrls[side] = data.croppedUrl;
            if (data.previewUrl) {
              this._previewUrls = this._previewUrls || {};
              this._previewUrls[side] = data.previewUrl;
            }
            // Cache the result
            if (sizeKey) {
              this._sizeUrlCache[sizeKey] = this._sizeUrlCache[sizeKey] || {};
              this._sizeUrlCache[sizeKey][side] = {
                croppedUrl: data.croppedUrl,
                previewUrl: data.previewUrl
              };
            }
            log(`Re-processed ${side}: ${data.croppedUrl}`);
          } else if (data.skipped) {
            log(`Re-processing skipped for ${side}: ${data.reason}`);
          }
        } catch (err) {
          console.warn(`[FileUploader] Re-processing failed for ${side}:`, err);
          // Non-fatal — original file still available
        }
      }
      this.emitStateChange();
    }

    showPreviewLoading(show, message = 'Processing...') {
      const previewContainer = this.container.querySelector('.large-preview-container');
      if (!previewContainer) return;

      let loadingOverlay = previewContainer.querySelector('.preview-loading-overlay');

      if (show) {
        if (!loadingOverlay) {
          // Create loading overlay
          loadingOverlay = document.createElement('div');
          loadingOverlay.className = 'preview-loading-overlay';
          loadingOverlay.innerHTML = `
            <div class="preview-loading-content">
              <div class="preview-loading-spinner"></div>
              <div class="preview-loading-text">${message}</div>
            </div>
          `;
          previewContainer.appendChild(loadingOverlay);
        } else {
          // Update message
          const textEl = loadingOverlay.querySelector('.preview-loading-text');
          if (textEl) textEl.textContent = message;
          loadingOverlay.style.display = 'flex';
        }
      } else {
        if (loadingOverlay) {
          loadingOverlay.style.display = 'none';
        }
      }
    }

    showError(message) {
      const errorsEl = this.container.querySelector('.upload-errors');
      if (!errorsEl) return;

      errorsEl.textContent = message;
      errorsEl.style.display = 'block';

      setTimeout(() => {
        errorsEl.style.display = 'none';
      }, 5000);
    }

    emitStateChange() {
      if (this.stateManager) {
        const uploadState = {
          mode: this.state.mode,
          sides: this.state.sides,
          shape: this.state.shape,
          uploads: this.state.uploads,
          isComplete: this.isUploadComplete()
        };

        // Emit event for backward compatibility
        this.stateManager.emit('file:upload:state-changed', uploadState);

        // ALWAYS update upload mode in StateManager (even when not complete)
        // This ensures mode changes are reflected immediately
        this.stateManager.setUploadMode(this.state.mode);

        // Update StateManager's upload state based on completion
        if (this.isUploadComplete()) {
          const uploadData = {
            uploadId: this.state.uploads.front?.uploadId || this.state.uploads.back?.uploadId,
            files: {
              front: this.state.uploads.front?.fileKey,
              back: this.state.uploads.back?.fileKey
            },
            previews: {
              front: this.state.uploads.front?.previewUrl,
              back: this.state.uploads.back?.previewUrl
            },
            croppedUrls: {
              front: this._croppedUrls?.front || null,
              back: this._croppedUrls?.back || null
            },
            previewUrls: {
              front: this._previewUrls?.front || null,
              back: this._previewUrls?.back || null
            },
            shape: this.state.shape,
            customConfig: this.state.customConfig || null,
            proofRequested: this.state.proofRequested || false,
            sides: this.state.sides,
            mode: this.state.mode
          };
          this.stateManager.setUploadData(uploadData);
        } else {
          // Clear upload data when switching to upload_now without files
          // This prevents validation from passing when user hasn't uploaded anything
          this.stateManager.clearUploadData();
        }
      }
    }

    isUploadComplete() {
      if (this.state.mode === 'send_later') return true;

      // Check for uploadId (set AFTER S3 upload + server processing complete)
      // NOT just non-null — preview generation sets the object early with only
      // thumbnailUrl/previewUrl, which would falsely signal completion
      if (this.state.sides === 'single') {
        return !!this.state.uploads.front?.uploadId;
      } else {
        return !!this.state.uploads.front?.uploadId && !!this.state.uploads.back?.uploadId;
      }
    }

    getState() {
      return {
        ...this.state,
        isComplete: this.isUploadComplete()
      };
    }

    reset() {
      log('Resetting file uploader to initial state');

      // Clear all file and upload state
      this.state.files = {
        front: null,
        back: null
      };
      this.state.uploads = {
        front: null,
        back: null
      };
      this.state.uploadProgress = {
        front: 0,
        back: 0
      };

      // Clear server-side URLs and size cache
      this._croppedUrls = {};
      this._previewUrls = {};
      this._sizeUrlCache = {};

      // Reset to default tab
      this.state.activeTab = 'front';

      // Keep mode and sides settings as user preference
      // this.state.mode and this.state.sides are intentionally not reset

      // Clear StateManager upload data
      if (this.stateManager && typeof this.stateManager.clearUploadData === 'function') {
        this.stateManager.clearUploadData();
      }

      // Hide any loading overlays
      this.showPreviewLoading(false);

      // Hide any error messages
      const errorsEl = this.container.querySelector('.upload-errors');
      if (errorsEl) {
        errorsEl.style.display = 'none';
        errorsEl.textContent = '';
      }

      // Re-render UI to show empty state
      this.render();
      this.attachEventListeners();

      log('File uploader reset complete');
    }
  }

  // Export to window
  window.CustomFileUploader = CustomFileUploader;

  log('Custom file uploader loaded');
})();
